import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { isFetching, error } from './common';
import { CommonRootState } from '../combineReducers';
import { fetchAll } from '@pec/aion-ui-odata/helpers/fetchAll';

/**
 * Represents a unique client that a subcontractor is connected to via SCM subcontracts.
 * Derived by aggregating clientSubcontracts across all subcontracts and deduplicating by clientId.
 * CPRO-4322: Used for Pendo metadata.
 */
export interface IScmConnectedClient {
  clientId: string;
  clientName: string;
}

interface ISubcontractResponse {
  id: string;
  primeContractorId: string;
  subcontractorId: string;
  numberOfClients: number;
  primeContractorName: string;
  subcontractorName: string;
}

interface IClientSubcontractResponse {
  id: string;
  clientId: string;
  subcontractId: string;
  clientName: string;
}

const name = 'scmConnectedClients';

/**
 * Fetches the list of connected clients for a given contractor organization.
 * CPRO-4322: Uses a two-step approach:
 *   1. GET subcontracts for the organization (isPrime=false)
 *   2. For each subcontract, GET clientSubcontracts
 *   3. Deduplicate clients across all subcontracts
 * Only fetches if not already fetching or loaded.
 */
export const fetchScmConnectedClients = createAsyncThunk(
  name,
  async (organizationId: string) => {
    // Step 1: Get all subcontracts for this organization
    const subcontractsResponse = await fetchAll<ISubcontractResponse>(
      `/api/v3.01/subcontractorManagement/subcontracts`,
      {
        params: {
          orgIdFilter: organizationId,
          isPrime: false
        }
      }
    );

    const subcontracts = subcontractsResponse.value;

    if (subcontracts.length === 0) {
      return [];
    }

    // Step 2: For each subcontract, fetch its client subcontracts in parallel
    const clientSubcontractResponses = await Promise.all(
      subcontracts.map(subcontract =>
        fetchAll<IClientSubcontractResponse>('/api/v3.01/subcontractorManagement/clientSubcontracts', {
          params: {
            $filter: `subcontractId eq ${subcontract.id}`
          }
        })
      )
    );

    // Step 3: Flatten and deduplicate clients by clientId
    const clientMap = new Map<string, IScmConnectedClient>();

    for (const response of clientSubcontractResponses) {
      for (const cs of response.value) {
        if (cs.clientId && !clientMap.has(cs.clientId)) {
          clientMap.set(cs.clientId, {
            clientId: cs.clientId,
            clientName: cs.clientName
          });
        }
      }
    }

    return Array.from(clientMap.values());
  },
  {
    condition(_, { getState }) {
      const { isFetching, isLoaded } = (getState() as CommonRootState).scmConnectedClients;
      return !isFetching && !isLoaded;
    }
  }
);

export type State = {
  isFetching: boolean;
  clients: IScmConnectedClient[];
  isLoaded: boolean;
  error: SerializedError | null;
};

export const initialState: State = {
  isFetching: false,
  clients: [],
  isLoaded: false,
  error: null
};

const scmConnectedClients = createSlice({
  name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchScmConnectedClients.pending, isFetching);
    builder.addCase(fetchScmConnectedClients.fulfilled, (state, action) => {
      state.isFetching = false;
      state.clients = action.payload ?? [];
      state.isLoaded = true;
    });
    builder.addCase(fetchScmConnectedClients.rejected, error);
  }
});

export const { reducer: scmConnectedClientsReducer } = scmConnectedClients;
